import { useEffect } from 'react';
import { X, Bell } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { useUnreadNotifications, useMarkRead, type InboxItem } from './api';

/**
 * Persistent "sintillo llamativo" banner under the TopBar.
 *
 * - Renders the most recent unread notification; if there are more, shows a
 *   `+N más` chip that anchors the user toward the Bell dropdown.
 * - **Los avisos de sesión no salen aquí.** Ver `SOLO_CAMPANITA`.
 * - Animated accent border (pulse) to draw the eye without screaming.
 * - X marks-as-read via the inapp source. ESC also dismisses.
 */
/**
 * Categorías que viven sólo en la campanita.
 *
 * Un aviso de sesión —«tu cuenta entró en otro dispositivo»— es información
 * útil, pero no merece un cartel fijo bajo la barra tapando aquello a lo que
 * el usuario acaba de entrar. Y es el aviso más frecuente de todos: aparece en
 * cada login desde otro sitio, así que en la práctica el banner estaba
 * permanentemente ocupado por él y empujaba lo demás detrás del `+N más`.
 *
 * Sigue llegando entero a la campanita, sin recortar: no se está ocultando
 * nada, se está eligiendo dónde se enseña.
 */
const SOLO_CAMPANITA = new Set(['session']);

export function NotificationBanner() {
  const t = useT();
  const { data: items } = useUnreadNotifications();
  const markRead = useMarkRead();

  /*
    El `+N más` cuenta lo mismo que se ve.

    Se filtra antes de contar a propósito: si el chip dijera «+6» incluyendo
    avisos que el banner nunca va a enseñar, quien lo pulsara encontraría una
    campanita que no cuadra con el número que acaba de leer.
  */
  const list: InboxItem[] = (items ?? []).filter(
    (i) => !SOLO_CAMPANITA.has(i.notification.category ?? ''),
  );
  const current = list[0];

  useEffect(() => {
    if (!current) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        markRead.mutate({ id: current.notificationId, source: 'inapp' });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [current, markRead]);

  if (!current) return null;

  const sender = current.notification.sender;
  const senderName =
    `${sender?.firstname ?? ''} ${sender?.lastname ?? ''}`.trim() ||
    sender?.email ||
    t('notifications.banner.systemSender');
  const initial = (sender?.firstname?.[0] ?? sender?.email?.[0] ?? '?').toUpperCase();
  const more = list.length - 1;

  return (
    <div className="sticky top-14 z-20 border-b border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-8 py-2">
      <div
        className={cn(
          'relative flex items-center gap-3 overflow-hidden rounded-xl border px-4 py-3',
          // Opaque surface, not an accent wash: the wash tinted the backdrop far
          // enough that the accent-ink label on top only reached ~4.1:1. The
          // accent border + shadow already carry the "this is urgent" read.
          'border-[var(--color-accent-ink)] bg-[var(--color-bg-surface)]',
          'animate-[notification-pulse_2.4s_ease-in-out_infinite] shadow-[0_0_0_1px_rgba(178,98,245,0.18),0_8px_24px_-12px_rgba(178,98,245,0.5)]',
        )}
        style={{ ['--tw-shadow-color' as never]: 'var(--color-xk-accent)' } as React.CSSProperties}
      >
        {/* Sender avatar */}
        <span
          aria-hidden="true"
          className="grid h-9 w-9 flex-none place-items-center rounded-full bg-[var(--color-xk-accent)] text-[12px] font-extrabold text-[var(--color-fg-on-accent)]"
        >
          {initial}
        </span>

        {/* Body */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Bell className="h-3 w-3 flex-none text-[var(--color-accent-ink)]" />
            <span
              className="truncate text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-accent-ink)]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {senderName}
            </span>
            <span className="text-[11px] text-[var(--color-xk-text-muted)]">·</span>
            <span className="truncate text-[12px] font-extrabold text-[var(--color-xk-lavender)]">
              {tituloDelAviso(current.notification, t)}
            </span>
          </div>
          <p className="mt-0.5 line-clamp-1 whitespace-pre-line text-[12px] text-[var(--color-xk-lavender)]/90">
            {cuerpoDelAviso(current.notification, t)}
          </p>
        </div>

        {/* +N más chip */}
        {more > 0 && (
          <span
            // Same reason as the banner above — a 16% accent fill under
            // accent-ink text measured 2.8:1 on every theme.
            className="flex-none rounded-full border border-[var(--color-accent-ink)] bg-[var(--color-bg-surface)] px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-accent-ink)]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            +{more} {t('notifications.banner.moreCount')}
          </span>
        )}

        {/* X mark-read */}
        <button
          type="button"
          onClick={() =>
            markRead.mutate({ id: current.notificationId, source: 'inapp' })
          }
          disabled={markRead.isPending}
          title={t('notifications.banner.markRead')}
          aria-label={t('notifications.banner.markRead')}
          className="grid h-7 w-7 flex-none place-items-center rounded-md text-[var(--color-xk-text-muted)] transition-colors hover:bg-[var(--color-xk-surface)] hover:text-[var(--color-xk-lavender)]"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* keyframes injected inline so we don't have to touch the Tailwind config */}
      <style>{`
        @keyframes notification-pulse {
          0%, 100% { box-shadow: 0 0 0 1px rgba(178,98,245,0.18), 0 8px 24px -12px rgba(178,98,245,0.5); }
          50%      { box-shadow: 0 0 0 2px rgba(178,98,245,0.45), 0 12px 32px -10px rgba(178,98,245,0.7); }
        }
      `}</style>
    </div>
  );
}

/*
 * Los avisos del SISTEMA se traducen; los que escribe una persona, no.
 *
 * El backend redacta el aviso de límite de plan en inglés y el banner lo pintaba
 * tal cual: una cuenta en español recibía «Plan limit critical — Your workspace
 * is at 100% of one of your plan limits» como primer mensaje del producto (D9 del
 * QA del viaje, 17 ago 2026). La categoría dice de qué va el aviso, así que aquí
 * se pone el texto en el idioma de quien mira. Sin categoría conocida se respeta
 * lo que venga: un aviso escrito por un compañero no se traduce.
 */
function tituloDelAviso(n: { title: string; category?: string | null }, t: (k: never) => string): string {
  if (n.category === 'plan_alert') return t('notifications.planAlert.title' as never);
  return n.title;
}

function cuerpoDelAviso(n: { body: string; category?: string | null }, t: (k: never) => string): string {
  if (n.category === 'plan_alert') return t('notifications.planAlert.body' as never);
  return n.body;
}
